import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../api-config";
import ChartPage1 from "./ChartPage1";
import ChartPage2 from "./ChartPage2";
import ChartPage3 from "./ChartPage3";

const TradeDashboard = () => {
  const { id } = useParams();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tradeType, setTradeType] = useState("all");
  const [lotRange, setLotRange] = useState([0.1, 5]); // rango inicial
  const [durationRange, setDurationRange] = useState([0, 9999]); // rango inicial en minutos
  const [chartPage, setChartPage] = useState(0);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const ids = id.split(",");
        const requests = ids.map((accountId) =>
          axios.get(`${API_URL}/trading-api/reports/trades/${accountId}`, {
            headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
          })
        );
        
        const responses = await Promise.all(requests);
        const allData = responses.flatMap((res) => res.data);

        const usdclp = allData.filter((t) => t.symbol === "USDCLP");
        const parsed = usdclp.map((t) => ({
          ...t,
          open: new Date(t.open_time),
          close: new Date(t.close_time),
          durationMin: Math.max(0, (new Date(t.close_time) - new Date(t.open_time)) / 60000),
        }));
        
        // Sort by open time to maintain chronological order when combining multiple accounts
        parsed.sort((a, b) => a.open - b.open);
        setTrades(parsed);

        if (usdclp.length > 0) {
          const vols = usdclp.map((t) => Number(t.volume));
          const min = Math.min(...vols);
          const max = Math.max(...vols);
          setLotRange([min, max]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchTrades();
  }, [id, token]);

  const filtered = useMemo(() => {
    return trades.filter((t) => {
      if (startDate) {
        const s = new Date(startDate);
        s.setHours(0, 0, 0, 0);
        if (t.open < s) return false;
      }
      if (endDate) {
        const e = new Date(endDate);
        e.setHours(23, 59, 59, 999);
        if (t.open > e) return false;
      }
      if (tradeType !== "all" && t.trade_type !== tradeType) return false;

      const v = Number(t.volume);
      if (v < lotRange[0] || v > lotRange[1]) return false;

      const d = Number(t.durationMin);
      if (d < durationRange[0] || d > durationRange[1]) return false;

      return true;
    });
  }, [trades, startDate, endDate, tradeType, lotRange, durationRange]);

  if (loading) {
    return (
      <p className="p-6 text-gray-400 bg-[#101523]">
        Cargando reporte...
      </p>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-[#101523] min-h-screen text-gray-200">
      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-4 items-end">
        <div>
          <label className="block text-sm mb-1">Fecha inicio:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full p-2 bg-[#1f2a40] rounded text-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Fecha fin:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full p-2 bg-[#1f2a40] rounded text-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Tipo operación:</label>
          <select
            value={tradeType}
            onChange={(e) => setTradeType(e.target.value)}
            className="w-full p-2 bg-[#1f2a40] rounded text-gray-200"
          >
            <option value="all">Todos</option>
            <option value="buy">Longs (Buy)</option>
            <option value="sell">Shorts (Sell)</option>
          </select>
        </div>
        {/* Inputs numéricos para rango de lotes */}
        <div className="flex flex-col">
          <label className="block text-sm mb-1">Rango de lotes:</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={lotRange[0]}
              onChange={(e) =>
                setLotRange([parseFloat(e.target.value) || 0.1, lotRange[1]])
              }
              className="w-20 p-2 bg-[#1f2a40] rounded text-gray-200"
            />
            <span className="self-center">—</span>
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={lotRange[1]}
              onChange={(e) =>
                setLotRange([lotRange[0], parseFloat(e.target.value) || lotRange[1]])
              }
              className="w-20 p-2 bg-[#1f2a40] rounded text-gray-200"
            />
          </div>
        </div>
        {/* Inputs numéricos para rango de duración */}
        <div className="flex flex-col">
          <label className="block text-sm mb-1">Duración (min):</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="1"
              value={durationRange[0]}
              onChange={(e) =>
                setDurationRange([parseFloat(e.target.value) || 0, durationRange[1]])
              }
              className="w-20 p-2 bg-[#1f2a40] rounded text-gray-200"
            />
            <span className="self-center">—</span>
            <input
              type="number"
              min="0"
              step="1"
              value={durationRange[1]}
              onChange={(e) =>
                setDurationRange([durationRange[0], parseFloat(e.target.value) || durationRange[1]])
              }
              className="w-20 p-2 bg-[#1f2a40] rounded text-gray-200"
            />
          </div>
        </div>
      </div>

      {/* Paginador */}
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setChartPage(0)}
          className={`px-3 py-1 rounded ${
            chartPage === 0 ? "bg-blue-600" : "bg-[#1f2a40]"
          } text-white`}
        >
          Página 1
        </button>
        <button
          onClick={() => setChartPage(1)}
          className={`px-3 py-1 rounded ${
            chartPage === 1 ? "bg-blue-600" : "bg-[#1f2a40]"
          } text-white`}
        >
          Página 2
        </button>
        <button
          onClick={() => setChartPage(2)}
          className={`px-3 py-1 rounded ${
            chartPage === 2 ? "bg-blue-600" : "bg-[#1f2a40]"
          } text-white`}
        >
          Página 3
        </button>
      </div>

      {/* Render de página */}
      {chartPage === 0 && <ChartPage1 filtered={filtered} />}
      {chartPage === 1 && <ChartPage2 filtered={filtered} />}
      {chartPage === 2 && <ChartPage3 data={filtered} />}
    </div>
  );
};

export default TradeDashboard;
